import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import useEmblaCarousel from 'embla-carousel-react';

/**
 * Open-window onboarding carousel: Embla with parallax/fade on scroll, and viewport height
 * derived from the active slide’s measured height (avoids the flex row using the tallest slide).
 *
 * @param {Object} [options]
 * @param {'start' | 'center' | 'end'} [options.align='start'] - Passed to Embla; start fits full-width slides.
 * @param {boolean | 'trimSnaps' | 'keepSnaps'} [options.containScroll=false] - Embla containScroll; false avoids merged snaps with auto-height-style layouts.
 * @param {boolean} [options.draggable=true] - Embla drag.
 * @param {number} [options.fadeSpeed=2.2] - Outgoing slide opacity falloff vs scroll distance.
 * @param {boolean} [options.loop=false] - Embla loop.
 * @param {number} [options.parallaxAmount=50] - Outgoing slide translateX strength (percent).
 * @returns {Object} Carousel state and controls: currentHeight, emblaApi, emblaRef, getSlideStyle, remeasureSlides, scrollNext, scrollTo, selectedIndex, slidesContainerRef.
 */
function useCarouselAnimation({
    align = 'start',
    containScroll = false,
    draggable = true,
    fadeSpeed = 2.2,
    loop = false,
    parallaxAmount = 50,
} = {}) {
    const [selectedIndex, setSelectedIndex] = useState(0);
    const [slideHeights, setSlideHeights] = useState([]);
    const [slideStyles, setSlideStyles] = useState([]);
    const slidesContainerRef = useRef(null);
    const [emblaRef, emblaApi] = useEmblaCarousel({
        align,
        containScroll,
        draggable,
        loop,
    });

    // Measure all slides on mount and after images load
    const measureSlides = useCallback(() => {
        if (!slidesContainerRef.current) return;
        const slides = slidesContainerRef.current.querySelectorAll('[data-slide]');
        const heights = Array.from(slides).map((slide) => slide.offsetHeight);
        setSlideHeights((prev) => {
            if (prev.length === heights.length && prev.every((h, i) => h === heights[i])) {
                return prev;
            }
            return heights;
        });
    }, []);

    const onSelect = useCallback(() => {
        if (!emblaApi) return;
        setSelectedIndex(emblaApi.selectedScrollSnap());
    }, [emblaApi]);

    // Calculate slide styles based on scroll progress
    const updateSlideStyles = useCallback(() => {
        if (!emblaApi) return;
        const scrollProgress = emblaApi.scrollProgress();
        const slidesCount = emblaApi.slideNodes().length;
        if (slidesCount < 2) {
            setSlideStyles([]);
            return;
        }
        const styles = emblaApi.slideNodes().map((_, index) => {
            // Calculate how far this slide is from the "center" of the viewport
            const slideProgress = index / Math.max(slidesCount - 1, 1) - scrollProgress;
            const distance = Math.abs(slideProgress);

            // Opacity: incoming slides stay at 1, outgoing slides fade to 0
            let opacity = 1;
            if (slideProgress < 0) {
                opacity = Math.max(0, 1 - distance * fadeSpeed);
            }

            // Z-index: slides ahead (positive slideProgress) are on top
            const zIndex = slideProgress >= 0 ? 10 : 1;

            // Parallax effect: outgoing slides move slower (lag behind)
            let translateX = 0;
            if (slideProgress < 0) {
                translateX = -slideProgress * parallaxAmount;
            }

            return { opacity, translateX, zIndex };
        });
        setSlideStyles(styles);
    }, [emblaApi, fadeSpeed, parallaxAmount]);

    useEffect(() => {
        if (!emblaApi) return undefined;
        onSelect();
        updateSlideStyles();
        emblaApi.on('select', onSelect);
        emblaApi.on('scroll', updateSlideStyles);
        emblaApi.on('reInit', updateSlideStyles);
        return () => {
            emblaApi.off('select', onSelect);
            emblaApi.off('scroll', updateSlideStyles);
            emblaApi.off('reInit', updateSlideStyles);
        };
    }, [emblaApi, onSelect, updateSlideStyles]);

    // Measure as soon as layout is committed (before paint) to avoid a visible height/layout flash.
    // Image onLoad still calls remeasureSlides for late layout changes.
    useLayoutEffect(() => {
        measureSlides();
    }, [measureSlides, emblaApi]);

    useEffect(() => {
        window.addEventListener('resize', measureSlides);
        return () => {
            window.removeEventListener('resize', measureSlides);
        };
    }, [measureSlides]);

    // Measure slides when the selected index changes
    useEffect(() => {
        const id = requestAnimationFrame(() => measureSlides());
        return () => cancelAnimationFrame(id);
    }, [selectedIndex, measureSlides]);

    const currentHeight = slideHeights[selectedIndex] || 'auto';

    const scrollNext = useCallback(() => {
        emblaApi?.scrollNext();
    }, [emblaApi]);

    const scrollTo = useCallback(
        (index) => {
            emblaApi?.scrollTo(index);
        },
        [emblaApi]
    );

    const getSlideStyle = useCallback(
        (index) => {
            const style = slideStyles[index] || { opacity: 1, translateX: 0, zIndex: 1 };
            return {
                opacity: style.opacity,
                transform: `translateX(${style.translateX}%)`,
                transition: 'opacity 100ms ease-out',
                zIndex: style.zIndex,
            };
        },
        [slideStyles]
    );

    return {
        currentHeight,
        emblaApi,
        emblaRef,
        getSlideStyle,
        remeasureSlides: measureSlides,
        scrollNext,
        scrollTo,
        selectedIndex,
        slidesContainerRef,
    };
}

export default useCarouselAnimation;
